import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Target, CheckCircle2, XCircle, ArrowRight, Trophy, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TrainerPage() {
  const { getAuthHeader } = useAuth();
  const [questions, setQuestions] = useState(null);
  const [stats, setStats] = useState({ open: 0, mastered: 0 });
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [sessionCorrect, setSessionCorrect] = useState(0);

  const load = useCallback(async () => {
    const [qRes, sRes] = await Promise.all([
      axios.get(`${API}/user/trainer/questions`, getAuthHeader()),
      axios.get(`${API}/user/trainer/stats`, getAuthHeader()),
    ]);
    setQuestions(qRes.data);
    setStats(sRes.data);
    setIdx(0);
  }, []);

  useEffect(() => { load(); }, [load]);

  const answer = async (i) => {
    if (result) return;
    setSelected(i);
    const res = await axios.post(`${API}/user/trainer/answer`,
      { question_id: questions[idx].question_id, answer_index: i }, getAuthHeader());
    setResult(res.data);
    if (res.data.correct) setSessionCorrect(c => c + 1);
  };

  const next = () => {
    setSelected(null);
    setResult(null);
    if (idx + 1 < questions.length) {
      setIdx(idx + 1);
    } else {
      load();
    }
  };

  if (questions === null) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 text-terra animate-spin" />
      </div>
    );
  }

  const q = questions[idx];

  return (
    <div className="max-w-3xl mx-auto animate-fade-in" data-testid="trainer-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-8 h-[2px] bg-terra" />
          <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-terra">
            Wiederholen, bis es sitzt
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-espresso leading-none tracking-tight uppercase">
          Fragen-<span className="text-terra">Trainer</span>
        </h1>
        <p className="text-espresso/70 mt-2">
          Deine falsch beantworteten Prüfungsfragen — beantworte sie richtig, um sie zu meistern.
        </p>
      </div>

      <div className="flex gap-3 mb-8">
        <div className="flex-1 bg-cream-100 border border-cream-300 rounded-2xl p-4 shadow-soft" data-testid="trainer-open-count">
          <p className="text-[10px] uppercase tracking-widest text-espresso/50 font-bold">Offen</p>
          <p className="font-display text-3xl text-terra">{stats.open}</p>
        </div>
        <div className="flex-1 bg-cream-100 border border-cream-300 rounded-2xl p-4 shadow-soft" data-testid="trainer-mastered-count">
          <p className="text-[10px] uppercase tracking-widest text-espresso/50 font-bold">Gemeistert</p>
          <p className="font-display text-3xl text-sage-dark">{stats.mastered}</p>
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="bg-cream-100 border border-cream-300 rounded-3xl p-12 text-center shadow-soft" data-testid="trainer-empty-state">
          <Trophy className="w-12 h-12 text-terra mx-auto mb-4" />
          <h2 className="font-display text-2xl text-espresso uppercase mb-2">Alles sitzt!</h2>
          <p className="text-espresso/70">
            {stats.mastered > 0
              ? `Stark — du hast ${stats.mastered} Frage${stats.mastered !== 1 ? 'n' : ''} gemeistert. Neue Fragen landen hier, wenn du in einem Quiz danebenliegst.`
              : 'Aktuell gibt es nichts zu wiederholen. Fragen landen hier automatisch, wenn du in einem Quiz danebenliegst.'}
          </p>
        </div>
      ) : (
        <div className="bg-cream-100 border border-cream-300 rounded-3xl p-6 sm:p-8 shadow-soft" data-testid="trainer-question-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <span className="text-[10px] uppercase tracking-widest text-espresso/50 font-bold">
              {q.course_title} · {q.module_title}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-terra font-bold">
              Frage {idx + 1} / {questions.length}
            </span>
          </div>
          <h2 className="text-lg font-semibold text-espresso mb-6" data-testid="trainer-question-text">{q.question}</h2>

          <div className="space-y-3">
            {q.options.map((opt, i) => {
              let cls = 'border-cream-300 bg-cream-50 hover:border-terra/50 hover:bg-cream-100';
              if (result) {
                if (i === result.correct_index) cls = 'border-sage-dark bg-sage-50 ring-1 ring-sage-dark/30';
                else if (i === selected) cls = 'border-red-400 bg-red-50';
                else cls = 'border-cream-200 bg-cream-50 opacity-60';
              } else if (i === selected) {
                cls = 'border-terra bg-terra/5';
              }
              return (
                <button
                  key={i}
                  onClick={() => answer(i)}
                  disabled={!!result}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-colors text-sm text-espresso flex items-start gap-3 ${cls}`}
                  data-testid={`trainer-option-${i}`}
                >
                  <span className="font-display text-terra mt-0.5">{String.fromCharCode(65 + i)}</span>
                  <span className="flex-1">{opt}</span>
                  {result && i === result.correct_index && <CheckCircle2 className="w-5 h-5 text-sage-dark flex-shrink-0" />}
                  {result && !result.correct && i === selected && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
                </button>
              );
            })}
          </div>

          {result && (
            <div className={`mt-6 p-4 rounded-2xl border ${result.correct ? 'bg-sage-50 border-sage-dark/30' : 'bg-red-50 border-red-200'}`} data-testid="trainer-feedback">
              <p className={`font-bold text-sm mb-1 ${result.correct ? 'text-sage-dark' : 'text-red-600'}`}>
                {result.correct ? '✓ Richtig — Frage gemeistert!' : '✗ Leider falsch — sie bleibt im Trainer.'}
              </p>
              {result.explanation && <p className="text-sm text-espresso/80">{result.explanation}</p>}
              <button
                onClick={next}
                className="mt-4 inline-flex items-center gap-2 bg-terra text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-terra-dark transition-colors"
                data-testid="trainer-next-btn"
              >
                {idx + 1 < questions.length ? 'Nächste Frage' : 'Runde abschließen'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {sessionCorrect > 0 && questions.length > 0 && (
        <p className="text-center text-xs text-espresso/50 mt-4 uppercase tracking-widest font-semibold">
          <Target className="w-3.5 h-3.5 inline mr-1 text-terra" />
          {sessionCorrect} in dieser Runde gemeistert
        </p>
      )}
    </div>
  );
}
